import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useContext,
  useLayoutEffect,
} from "react";
import {
  motion,
  useTransform,
  transform,
  useMotionValue,
  PanInfo,
} from "framer-motion";

import WheelSizeContext from "../../contexts/WheelSizeContext";
import LifeAreasContext from "../../contexts/LifeAreasContext";
import ColourContext from "../../contexts/ColourContext";

import style from "./AreasScore.module.css";
import AreaIcon from "../areaIcon/AreaIcon";

import { colourThemes } from "../../styles/colourPalettes";
import lifeAreasData from "../../resources/lifeAreas";

import upDownChevron from "../../assets/icons/up-down-chevron.svg";

type AreasScoreProps = {};

const AreasScore = (props: AreasScoreProps) => {
  // Colour Theme
  const { key: colourKey } = useContext(ColourContext);
  const colour = colourThemes[colourKey];
  // Areas
  const { lifeAreas: areas, dispatchLifeAreas } = useContext(LifeAreasContext);
  // Wheel is made twice as large as 50% is hidden.
  const wheelSize = useContext(WheelSizeContext);
  const dragHandleHeight = 100;

  // Key of the area being scored
  const [activeKey, setActiveKey] = useState(areas.keys[0]);
  // Local score used for displaying within component as the slider moves. On drag end the value is passed to reducer.
  const [localScore, setLocalScore] = useState(
    areas.scores[areas.keys[0]] || 50
  );
  const dragContainer = useRef<HTMLDivElement>(null);
  const [dragContainerHeight, setDragContainerHeight] = useState(wheelSize);

  const dragY = useMotionValue(0);
  const circleOpacity = useTransform(dragY, [0, dragContainerHeight], [1, 0]);

  useLayoutEffect(() => {
    if (!dragContainer.current) return;
    const height = dragContainer.current.getBoundingClientRect().height;
    setDragContainerHeight(height);
  }, [dragContainer]);

  // This sets the y value correctly on load from the existing score.
  const updateYMotionValue = useCallback(
    (value) => {
      const yVal = transform(
        value,
        [0, 10],
        [dragContainerHeight - dragHandleHeight, 0]
      );
      dragY.set(yVal);
    },
    [dragY, dragContainerHeight]
  );

  // When the index changes update the local score to match the one from context
  useEffect(() => {
    const score = areas.scores[activeKey] || 5.0;
    setLocalScore(score);
    updateYMotionValue(score);
  }, [areas, activeKey, updateYMotionValue]);

  const onDrag = (e: any, info: PanInfo) => {
    const val = transform(
      info.point.y,
      [0, dragContainerHeight - dragHandleHeight],
      [10, 0]
    );
    window.requestAnimationFrame(() => {
      setLocalScore(val);
    });
  };

  const updateScore = useCallback(() => {
    dispatchLifeAreas({
      type: "SET_AREA_SCORE",
      payload: { key: activeKey, score: localScore },
    });
  }, [activeKey, localScore, dispatchLifeAreas]);

  const onDragStart = useCallback((e: any) => {
    e.target.style.cursor = "grabbing";
  }, []);

  const onDragEnd = useCallback(
    (e: any) => {
      e.target.style.cursor = "grab";
      updateScore();
    },
    [updateScore]
  );

  const handleAreaClicked = useCallback((key) => {
    setActiveKey(key);
  }, []);

  return (
    <div className={style.container}>
      <div className={style.areas}>
        {areas.keys.map((key) => (
          <AreaIcon
            key={key}
            area={lifeAreasData[key]}
            isHighlighted={activeKey === key}
            onTap={() => handleAreaClicked(key)}
          />
        ))}
      </div>
      <div className={style.areaInfo}>
        <h3>{lifeAreasData[activeKey].title}</h3>
        <h3>{localScore.toFixed(1)}</h3>
      </div>

      <div ref={dragContainer} className={style.dragContainer}>
        <div
          className={style.circle}
          style={{
            top: dragHandleHeight / 2,
            width: dragContainerHeight * 2,
            height: dragContainerHeight * 2,
          }}
        >
          <motion.div
            style={{
              backgroundColor: colour,
              y: dragY,
              opacity: circleOpacity,
            }}
          />
        </div>
        <motion.div
          className={style.dragHandle}
          drag="y"
          dragConstraints={dragContainer}
          dragElastic={0.5}
          dragMomentum={false}
          style={{ height: dragHandleHeight, y: dragY }}
          onDrag={onDrag}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <img src={upDownChevron} alt="direction" />
        </motion.div>
      </div>
    </div>
  );
};

export default AreasScore;

//   const startScoringArea = (key: string, areaRotation: number) => {
//     const rotation = areaRotation;
//     let rotateWheel = 0 - rotation;
//     // If greater than 180 rotate the other way!
//     console.log("(startScoringArea) current wheelRotation", wheelRotation);
//     console.log("(startScoringArea) rotate wheel", rotateWheel);
//     // if (Math.abs(rotateWheel) > 180) {
//     //   console.log("dont over rotate!")
//     //   rotateWheel = 360 - Math.abs(rotateWheel)
//     // }
//     console.log("final rotate wheel", rotateWheel);
//     setScoringAreaKey(key);
//     setWheelRotation(rotateWheel);
//   };
// const isScoringArea = isScoring && scoringAreaKey === key;
// const areaScore = (areas.scores && areas.scores[key]) || 65;
